import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { audiencesApi } from '../../api/audiences';
import Modal from '../../components/base/Modal';
import ModalErrorMessage from '../../components/base/Modal/ModalErrorMessage';
import ModalFooter from '../../components/base/Modal/ModalFooter';
import ModalSection from '../../components/base/Modal/ModalSection';
import ModalTitle from '../../components/base/Modal/ModalTitle';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../components/base/Notifications';
import styles from './Contacts.module.scss';

const AddContactModal = ({ closeModal, showNavigationModal, setContactId }) => {
  const [createContact, createContactResult] =
    audiencesApi.useCreateContactMutation();

  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    wallet: '',
    email: '',
    phone: '',
    twitter: '',
    discord: '',
    telegram: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if ((name === 'wallet' || name === 'email') && errors?.mandatory) {
      setErrors({});
    }
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.wallet && !formData.email) {
      newErrors.mandatory = 'Either Wallet or Email is required.';
    }
    if (
      formData.email &&
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)
    ) {
      newErrors.email = 'Invalid email address.';
    }
    if (formData.phone && !/^\+?[0-9]\d{1,14}$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number.';
    }
    if (formData.twitter && !/^[A-Za-z0-9_]{1,15}$/.test(formData.twitter)) {
      newErrors.twitter = 'Invalid Twitter handle.';
    }
    if (formData.discord && !/^.{2,32}#\d{4}$/.test(formData.discord)) {
      newErrors.discord = 'Invalid Discord ID. Use username#1234 format.';
    }
    if (formData.telegram && !/^[a-zA-Z0-9_]{5,32}$/.test(formData.telegram)) {
      newErrors.telegram = 'Invalid Telegram ID.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      createContact(formData);
    }
  };

  useEffect(() => {
    if (createContactResult.status === 'fulfilled') {
      const newContactId = createContactResult.data?.alid;
      if (newContactId) {
        setContactId(newContactId);
      }
      showSuccessMessage('Contact successfully created');
      closeModal();
      showNavigationModal();
    } else if (createContactResult.status === 'rejected') {
      showErrorMessage('Error during contact creation, please try again');
    }
  }, [closeModal, createContactResult, showNavigationModal]);

  return (
    <Modal
      onCancel={closeModal}
      loading={createContactResult.status === 'pending'}
    >
      <ModalTitle title="Add Contact" />

      <ModalSection title="First Name">
        <div className={styles.input_wrapper}>
          <input
            type="text"
            placeholder="Enter first name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
          />
        </div>
      </ModalSection>

      <ModalSection title="Last Name">
        <div className={styles.input_wrapper}>
          <input
            type="text"
            placeholder="Enter last name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
          />
        </div>
      </ModalSection>

      <ModalSection title="Wallet">
        <div className={styles.input_wrapper}>
          <input
            type="text"
            placeholder="Enter wallet address"
            name="wallet"
            value={formData.wallet}
            onChange={handleChange}
          />
        </div>
        <ModalErrorMessage
          show={errors?.mandatory}
          message={errors?.mandatory}
          className={styles.error_message}
        />
      </ModalSection>

      <ModalSection title="Email">
        <div className={styles.input_wrapper}>
          <input
            type="text"
            placeholder="Enter email address"
            name="email"
            value={formData.email}
            onChange={handleChange}
          />
        </div>
        <ModalErrorMessage
          show={errors?.mandatory}
          message={errors?.mandatory}
          className={styles.error_message}
        />
        <ModalErrorMessage
          show={errors?.email}
          message={errors?.email}
          className={styles.error_message}
        />
      </ModalSection>

      <ModalSection title="Phone">
        <div className={styles.input_wrapper}>
          <input
            type="text"
            placeholder="Enter phone number"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
          />
        </div>
        <ModalErrorMessage
          show={errors?.phone}
          message={errors?.phone}
          className={styles.error_message}
        />
      </ModalSection>

      <ModalSection title="Twitter">
        <div className={styles.input_wrapper}>
          <input
            type="text"
            placeholder="Enter Twitter id"
            name="twitter"
            value={formData.twitter}
            onChange={handleChange}
          />
        </div>
        <ModalErrorMessage
          show={errors?.twitter}
          message={errors?.twitter}
          className={styles.error_message}
        />
      </ModalSection>

      <ModalSection title="Discord">
        <div className={styles.input_wrapper}>
          <input
            type="text"
            placeholder="Enter Discord id"
            name="discord"
            value={formData.discord}
            onChange={handleChange}
          />
        </div>
        <ModalErrorMessage
          show={errors?.discord}
          message={errors?.discord}
          className={styles.error_message}
        />
      </ModalSection>

      <ModalSection title="Telegram">
        <div className={styles.input_wrapper}>
          <input
            type="text"
            placeholder="Enter Telegram id"
            name="telegram"
            value={formData.telegram}
            onChange={handleChange}
          />
        </div>
        <ModalErrorMessage
          show={errors?.telegram}
          message={errors?.telegram}
          className={styles.error_message}
        />
      </ModalSection>

      <ModalFooter>
        <button
          type="button"
          className="outline-button border border-1"
          onClick={closeModal}
        >
          Cancel
        </button>
        <button type="button" className="regular-button" onClick={handleSubmit}>
          Save
        </button>
      </ModalFooter>
    </Modal>
  );
};

const AddContactNavigationModal = ({ closeModal, contactId }) => {
  const navigate = useNavigate();

  const handleGoToProfile = () => {
    navigate(`/profile/${contactId}`);
  };

  return (
    <Modal onCancel={closeModal}>
      <ModalTitle title="Contact created" />

      <div className={styles.modal_description}>
        <span>The new contact was successfully created.</span>
      </div>

      <ModalFooter>
        <button
          type="button"
          className="outline-button border border-1"
          onClick={closeModal}
        >
          Close
        </button>
        <button
          type="button"
          className="regular-button"
          onClick={handleGoToProfile}
          disabled={!contactId}
        >
          Go to Profile
        </button>
      </ModalFooter>
    </Modal>
  );
};

const AddContact = () => {
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [newContactId, setNewContactId] = useState(null);

  const handleShowNavigationModal = () => {
    setShowAddContactModal(false);
    setShowNavigationModal(true);
  };

  const handleCloseNavigationModal = () => {
    setShowNavigationModal(false);
    setNewContactId(null);
  };

  return (
    <>
      <button
        className={`regular-button ${styles.add_contact_button}`}
        type="button"
        onClick={() => setShowAddContactModal(true)}
      >
        Add a Contact
      </button>
      {showAddContactModal && (
        <AddContactModal
          closeModal={() => setShowAddContactModal(false)}
          showNavigationModal={handleShowNavigationModal}
          setContactId={setNewContactId}
        />
      )}
      {showNavigationModal && (
        <AddContactNavigationModal
          closeModal={handleCloseNavigationModal}
          contactId={newContactId}
        />
      )}
    </>
  );
};

export default AddContact;
